import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
interface ProjectCardProps {
  title: string;
  badges: string[];
  description: string;
  image: string;
  index: number;
  role: string;
  duration: string;
  tools: string;
  industry: string;
  href?: string;
}
export function ProjectCard({
  title,
  badges,
  description,
  image,
  index,
  role,
  duration,
  tools,
  industry,
  href = '#'
}: ProjectCardProps) {
  const {
    ref,
    isVisible
  } = useScrollReveal(0.2);
  // Determine if this is an internal route (starts with /)
  const isInternalLink = href.startsWith('/');
  const cardContent = <>
      {/* Background Image with Parallax-like scale on hover */}
      <div className="absolute inset-0 bg-gray-900 transition-transform duration-700 group-hover:scale-105" data-id="element-222">
        <img src={image} alt={title} className="w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity duration-500" data-id="element-223" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" data-id="element-224" />
      </div>

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-16 flex flex-col justify-between pointer-events-none" data-id="element-225">
        {/* Spacer for top breathing room on mobile */}
        <div className="flex-shrink-0" data-id="element-226" />

        <div className="transform transition-transform duration-500 md:translate-y-8 group-hover:translate-y-0" data-id="element-227">
          <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-228">
            <div className="flex flex-wrap gap-2" data-id="element-229">
              {badges.map((badge, i) => <span key={i} className="inline-block px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-xs md:text-sm text-white font-medium" data-id="element-230">
                  {badge}
                </span>)}
            </div>
          </div>

          {/* Title and Button Container */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6 mb-4" data-id="element-231">
            <h3 className="text-3xl md:text-6xl font-bold text-white tracking-tight flex-1" data-id="element-232">
              {title}
            </h3>

            <div className="pointer-events-auto flex-shrink-0" data-id="element-233">
              <div className="inline-flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 rounded-full bg-white text-black transition-all duration-300 hover:scale-105 hover:shadow-lg" data-id="element-234">
                <span className="text-xs md:text-sm font-semibold whitespace-nowrap" data-id="element-235">
                  View Project
                </span>
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:rotate-45" data-id="element-236" />
              </div>
            </div>
          </div>

          <div className="space-y-6 md:space-y-8 md:opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100" data-id="element-237">
            <p className="text-base md:text-lg text-gray-300 max-w-xl" data-id="element-238">
              {description}
            </p>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-4 md:pt-6 border-t border-white/10" data-id="element-239">
              <div data-id="element-240">
                <h4 className="text-xs md:text-sm text-gray-500 mb-1" data-id="element-241">Role</h4>
                <p className="text-sm md:text-base text-white font-medium" data-id="element-242">
                  {role}
                </p>
              </div>
              <div data-id="element-243">
                <h4 className="text-xs md:text-sm text-gray-500 mb-1" data-id="element-244">
                  Duration
                </h4>
                <p className="text-sm md:text-base text-white font-medium" data-id="element-245">
                  {duration}
                </p>
              </div>
              <div data-id="element-246">
                <h4 className="text-xs md:text-sm text-gray-500 mb-1" data-id="element-247">Tools</h4>
                <p className="text-sm md:text-base text-white font-medium" data-id="element-248">
                  {tools}
                </p>
              </div>
              <div data-id="element-249">
                <h4 className="text-xs md:text-sm text-gray-500 mb-1" data-id="element-250">
                  Industry
                </h4>
                <p className="text-sm md:text-base text-white font-medium" data-id="element-251">
                  {industry}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
  const cardClassName = `group relative block w-full min-h-[600px] md:min-h-[800px] rounded-[32px] overflow-hidden transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-24 scale-95'}`;
  if (isInternalLink) {
    return <Link to={href} ref={ref} className={cardClassName} data-id="element-252">
        {cardContent}
      </Link>;
  }
  return <a href={href} ref={ref} className={cardClassName} data-id="element-253">
      {cardContent}
    </a>;
}